import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Modal } from 'antd'
import { TrashIcon, PlusIcon, BanIcon, CheckCircleIcon } from '@heroicons/react/outline'

import { Button, makeRowId } from '../../../../components'
import { fetchLastmileXdocks, syncZoneCosts } from '../../../../services'
import { costIsPositiveInCents, currencyFor, isSpecialZone, tierRangeFrom, weightSortKey, zoneBoundXdockId } from './costHelpers'

const T_PREFIX = 'lastmile_management.shipping_costs.edit_zone'
const EMPTY_XDOCKS = []

/**
 * Per-zone cost editor: read-only zone, cross-dock select (Cubbo), and a
 * DESDE/HASTA/COSTO ranges table with add/delete. Saves the whole zone in one
 * request via PUT /shipping-costs/zone (upsert tiers + delete removed + reassign xdock).
 */
export const EditZoneCostsModal = ({ zone, shippingMethodId, country, onClose, t }) => {
  const isOpen = !!zone
  const isCubbo = !!zone?.is_cubbo
  const queryClient = useQueryClient()
  const currency = currencyFor(country)

  const [rows, setRows] = useState([])
  const [xdockId, setXdockId] = useState(null)

  const { data: xdocksData } = useQuery(['lastmile-xdocks', country], () => fetchLastmileXdocks({ country, includeZones: true }), {
    enabled: isOpen && isCubbo && Boolean(country),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
  const xdocks = xdocksData || EMPTY_XDOCKS
  const xdockOptions = useMemo(() => xdocks.map(x => ({ label: x.name, value: x.id })), [xdocks])

  // Seed the ranges from the zone's tiers whenever the modal opens for a zone. This has no
  // per-zone ref guard, so it relies on the parent holding `zone` in state (a stable reference
  // that only changes on a genuine selection) — never passing a freshly-built object each render.
  useEffect(() => {
    if (!zone) return
    const tiers = [...(zone.tiers || [])].sort((a, b) => weightSortKey(a.max_compound_weight) - weightSortKey(b.max_compound_weight))
    setRows(
      tiers.map(ti => ({
        __rid: makeRowId(),
        id: ti.shipping_cost_id,
        max: String(ti.max_compound_weight ?? ''),
        cost: String(ti.cost ?? ''),
        enabled: ti.coverage_enabled !== false,
      })),
    )
  }, [zone])

  // Resolve the zone's current cross-dock once the xdocks load. Sets a PRIMITIVE, so
  // React's Object.is bailout stops it from looping when the xdocks reference changes.
  useEffect(() => {
    if (!zone) return
    setXdockId(zoneBoundXdockId(xdocks, zone.zone))
  }, [xdocks, zone])

  const setRow = (i, key, val) => setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)))
  // A new range is always the open-ended TOP band, so it only makes sense once the current last
  // range has a ceiling — otherwise you'd stack two tiers with no upper bound. Gate the button on it.
  const canAddRange = rows.length === 0 || String(rows[rows.length - 1]?.max ?? '').trim() !== ''
  const addRow = () => {
    if (!canAddRange) return
    setRows(rs => [...rs, { __rid: makeRowId(), max: '', cost: '', enabled: true }])
  }
  const removeRow = i => setRows(rs => rs.filter((_, idx) => idx !== i))
  const toggleEnabled = i => setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, enabled: r.enabled === false } : r)))

  const mutation = useMutation(payload => syncZoneCosts(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['tarif_zones_status'])
      queryClient.invalidateQueries(['lastmile-xdocks'])
      onClose?.()
    },
  })

  // The DESDE bound is derived from the sorted ceilings of the ENABLED rows (a disabled tier
  // isn't runtime-selectable, so it shouldn't shift a neighbor's band), so a tier added after
  // seeding or an edited weight still reads as a correct range, not "20 – 3 kg".
  const rangeFrom = max => tierRangeFrom(rows, max)

  // Validation gates. ALL tiers (enabled or disabled) must carry a positive ceiling:
  // sync_zone requires it for every tier, and a blank ceiling on a disabled legacy (NULL)
  // tier would otherwise be silently rewritten to 0 and can collide on the unique
  // (zone, weight) index. Enabled tiers additionally need cost > 0. Ceilings must be
  // distinct across the zone (same rule as the bulk wizards' duplicate_tier). For Cubbo
  // zones an xdock is required so the zone stays routable.
  //
  // A zone may be left with ALL tiers disabled — that retires its pricing (e.g. before
  // disabling the coverage), and the backend accepts it. So gate on rows.length (sync_zone
  // rejects an EMPTY tiers array) rather than enabledRows.length; disabling the last active
  // tier must still be saveable.
  const enabledRows = rows.filter(r => r.enabled !== false)
  const allMaxes = rows.map(r => Number(r.max))
  const hasDuplicateMax = new Set(allMaxes).size !== allMaxes.length
  const tiersComplete = rows.every(r => Number(r.max) > 0 && Number.isFinite(Number(r.cost)))
  const enabledCostsPositive = enabledRows.every(r => costIsPositiveInCents(r.cost))
  // Special zones (NONE_APPLIES / EXTENDED_ZONE) are exempt from the xdock requirement.
  const missingXdock = isCubbo && !isSpecialZone(zone?.zone) && !xdockId
  const canSave = rows.length > 0 && tiersComplete && enabledCostsPositive && !hasDuplicateMax && !missingXdock && !mutation.isLoading

  // "incomplete" before "duplicate": two blank ceilings are Number('')===0, which also trips
  // hasDuplicateMax — but the real problem is the empty fields, not a collision.
  const saveHint = missingXdock
    ? t(`${T_PREFIX}.hint_xdock`)
    : !tiersComplete
      ? t(`${T_PREFIX}.incomplete_hint`)
      : hasDuplicateMax
        ? t(`${T_PREFIX}.hint_duplicate_weight`)
        : t(`${T_PREFIX}.incomplete_hint`)

  const handleSave = () => {
    mutation.mutate({
      shipping_method_id: Number(shippingMethodId),
      tarif_zone: zone.zone,
      currency_code: currency || undefined,
      ...(isCubbo && xdockId ? { xdock_id: xdockId } : {}),
      tiers: [...rows]
        .sort((a, b) => weightSortKey(a.max) - weightSortKey(b.max))
        .map(r => ({
          ...(r.id ? { id: r.id } : {}),
          max_compound_weight: Number(r.max),
          cost: Number(r.cost),
          coverage_enabled: r.enabled !== false,
        })),
    })
  }

  const field = 'block w-full rounded-md border-gray-300 shadow-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
  const label = 'mb-1 block text-xs font-medium text-gray-700'

  return (
    <Modal open={isOpen} onCancel={onClose} className="cost-modal" title={t(`${T_PREFIX}.title`)} footer={null} width={720} destroyOnClose>
      <div className="space-y-4">
        <div>
          <label className={label}>
            {t(`${T_PREFIX}.zone`)} <span className="text-red-500">*</span>
          </label>
          <input type="text" className={`${field} bg-gray-50 text-gray-500`} value={zone?.zone || ''} disabled />
        </div>

        {isCubbo && (
          <div>
            <label className={label}>
              {t(`${T_PREFIX}.xdock`)} <span className="text-red-500">*</span>
            </label>
            <select className={field} value={xdockId ?? ''} onChange={e => setXdockId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">{t(`${T_PREFIX}.xdock_placeholder`)}</option>
              {xdockOptions.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-semibold text-gray-900">{t(`${T_PREFIX}.ranges_title`)}</p>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_from`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_to`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${T_PREFIX}.col_cost`)}</th>
                  <th className="w-12 px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((r, i) => {
                  const disabled = r.enabled === false
                  const from = rangeFrom(r.max)
                  return (
                    <tr key={r.__rid} className={disabled ? 'bg-gray-50 opacity-60' : ''}>
                      <td className={`px-4 py-2 text-gray-600 ${disabled ? 'line-through' : ''}`}>
                        {from == null ? t(`${T_PREFIX}.from_zero`) : t(`${T_PREFIX}.from_more`, { weight: from })}
                      </td>
                      <td className="px-4 py-2">
                        <div className="relative w-40">
                          {/* The ceiling stays editable even when the tier is disabled: a legacy tier
                              seeded with a NULL/blank ceiling could otherwise never be corrected, blocking
                              every save for the zone (sync_zone requires a ceiling on every tier). */}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={`${field} pr-9`}
                            value={r.max}
                            placeholder={t(`${T_PREFIX}.to_placeholder`)}
                            onChange={e => setRow(i, 'max', e.target.value)}
                          />
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 sm:text-sm">kg</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="relative w-40">
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 sm:text-sm">{currency}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={`${field} pl-12`}
                            value={r.cost}
                            disabled={disabled}
                            placeholder="0.00"
                            onChange={e => setRow(i, 'cost', e.target.value)}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {r.id ? (
                          <button
                            type="button"
                            onClick={() => toggleEnabled(i)}
                            className="p-1 text-gray-400 transition-colors hover:text-cubbo-700"
                            title={disabled ? t(`${T_PREFIX}.enable`) : t(`${T_PREFIX}.disable`)}
                          >
                            {disabled ? <CheckCircleIcon className="h-5 w-5" /> : <BanIcon className="h-5 w-5" />}
                          </button>
                        ) : (
                          <button type="button" onClick={() => removeRow(i)} className="p-1 text-gray-400 hover:text-red-600" title={t(`${T_PREFIX}.remove`)}>
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={!canAddRange}
            title={canAddRange ? undefined : t(`${T_PREFIX}.add_range_disabled_hint`)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <PlusIcon className="h-4 w-4" />
            {t(`${T_PREFIX}.add_range`)}
          </button>
        </div>

        {mutation.isError && <p className="text-sm text-red-600">{mutation.error?.response?.data?.error || t(`${T_PREFIX}.error`)}</p>}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
          {!canSave && !mutation.isLoading && rows.length > 0 && <p className="text-xs text-gray-400">{saveHint}</p>}
          <Button type="primary" disabled={!canSave} onClick={handleSave}>
            {mutation.isLoading ? t(`${T_PREFIX}.saving`) : t(`${T_PREFIX}.save`)}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
